import React from "react";
import { canonical } from "../data/business";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import QuoteForm from "../components/QuoteForm";
import FaqAccordion from "../components/FaqAccordion";
import { services } from "../data/services";
import { locations, findLocation } from "../data/locations";
import { faqs } from "../data/faqs";

export default function LocationDetail() {
  const { slug } = useParams();
  const l = findLocation(slug);
  if (!l) return <Navigate to="/locations" replace />;

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "LocalBusiness", "name": `Last Cut Landscaping — ${l.name}`, "areaServed": l.name, "description": `Premium landscaping in ${l.name}, ${l.region}`, "address": { "@type": "PostalAddress", "addressLocality": l.name, "addressRegion": "Scotland", "addressCountry": "GB" } },
    { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqs.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) },
  ];

  return (
    <div className="page-enter">
      <Seo title={`Landscapers ${l.name} — Premium Landscaping Company | Last Cut`} description={`Premium landscapers covering ${l.name}, ${l.region}. Artificial grass, patios, driveways, decking, fencing & garden design. Free quotes, fully insured.`} canonical={canonical(`/locations/${l.slug}`)} jsonLd={jsonLd} />

      <section className="bg-cream pt-8 lg:pt-12 pb-12 lg:pb-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Breadcrumbs items={[{ label: "Areas", to: "/locations" }, { label: l.name }]} />
          <div className="grid lg:grid-cols-12 gap-10 mt-6 items-center">
            <div className="lg:col-span-6">
              <div className="pill mb-4"><MapPin size={12}/> {l.region} · {l.postcode}</div>
              <h1 className="text-display font-display text-[clamp(2.4rem,5vw,4.5rem)] text-dark">Landscapers <span className="italic text-[color:var(--lc-green)]">{l.name}</span></h1>
              <p className="text-lg text-dark/75 mt-5">{l.intro}</p>
              <div className="flex flex-wrap gap-3 mt-7">
                <Link to="/quote" className="btn-primary">Free quote in {l.name} <ArrowRight size={16}/></Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {l.landmarks.slice(0, 6).map((lm) => <div key={lm} className="pill">{lm}</div>)}
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-3xl overflow-hidden aspect-[4/3]"><img src={l.image} alt={`Landscaping projects in ${l.name}, Scotland`} className="w-full h-full object-cover"/></div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="eyebrow mb-3">Services in {l.name}</div>
          <h2 className="font-display text-3xl lg:text-5xl text-dark mb-8">Everything we do in {l.name}.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {services.map((s) => (
              <Link key={s.slug} to={`/${s.slug}/${l.slug}`} className="lc-card p-5">
                <div className="font-display text-lg text-dark">{s.name}</div>
                <div className="text-xs text-dark/60 mt-1">in {l.name}</div>
                <div className="mt-3 text-[color:var(--lc-green)] text-sm font-semibold flex items-center gap-1">Learn more <ArrowRight size={14}/></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-paper">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-3">Neighbourhoods we cover</div>
            <h2 className="font-display text-3xl lg:text-4xl text-dark mb-5">Across {l.name} and beyond</h2>
            <div className="flex flex-wrap gap-2">
              {l.neighborhoods.map((n) => <span key={n} className="px-3 py-1.5 bg-white rounded-full border border-lc text-sm text-dark/80">{n}</span>)}
            </div>
            <p className="text-dark/70 mt-6 leading-relaxed">If you're nearby and unsure, just call — we cover all of {l.region} and surrounding postcodes.</p>
          </div>
          <div className="lg:col-span-5">
            <QuoteForm defaultService="" sourcePage={`/locations/${l.slug}`} />
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <h2 className="font-display text-3xl lg:text-5xl text-dark text-center mb-8">{l.name} landscaping FAQs</h2>
          <FaqAccordion items={faqs} />
        </div>
      </section>
    </div>
  );
}
